import React from 'react';

interface GanttTimelineProps {
  startDate: Date;
  endDate: Date;
}

export const GanttTimeline: React.FC<GanttTimelineProps> = ({ startDate, endDate }) => {
  // Generate month markers
  const generateMonthMarkers = () => {
    const markers = [];
    const current = new Date(startDate);
    current.setDate(1); // Start from the first day of the month
    
    while (current <= endDate) {
      const monthName = current.toLocaleDateString('pt-BR', { month: 'short' });
      const year = current.getFullYear();
      const isNewYear = current.getMonth() === 0;
      
      markers.push({
        date: new Date(current),
        label: isNewYear ? `${monthName} ${year}` : monthName,
        isNewYear
      });
      
      current.setMonth(current.getMonth() + 1);
    }
    
    return markers;
  };

  // Generate week markers for more granular view
  const generateWeekMarkers = () => {
    const markers = [];
    const current = new Date(startDate);
    
    // Start from the beginning of the week
    const dayOfWeek = current.getDay();
    current.setDate(current.getDate() - dayOfWeek);
    
    while (current <= endDate) {
      const weekNumber = getWeekNumber(current);
      const monthName = current.toLocaleDateString('pt-BR', { month: 'short' });
      
      markers.push({
        date: new Date(current),
        label: `S${weekNumber}`,
        month: monthName
      });
      
      current.setDate(current.getDate() + 7);
    }
    
    return markers;
  };

  // Get week number
  const getWeekNumber = (date: Date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  // Calculate total days
  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // Determine if we should show weeks or months based on date range
  const showWeeks = totalDays <= 90; // Show weeks for 3 months or less
  
  const markers = showWeeks ? generateWeekMarkers() : generateMonthMarkers();

  return (
    <div className="relative h-16 border-b border-white/10">
      {/* Background grid */}
      <div className="absolute inset-0 grid grid-cols-12 gap-0">
        {Array.from({ length: 12 }).map((_, index) => (
          <div key={index} className="border-r border-white/5 last:border-r-0" />
        ))}
      </div>
      
      {/* Month/Week markers */}
      <div className="relative h-full">
        {markers.map((marker, index) => {
          const position = Math.max(0, Math.min(100, 
            ((marker.date.getTime() - startDate.getTime()) / (endDate.getTime() - startDate.getTime())) * 100
          ));
          
          return (
            <div
              key={index}
              className="absolute top-0 bottom-0 flex flex-col justify-center border-l border-white/20"
              style={{ left: `${position}%` }}
            >
              <div className="ml-2">
                <div className={`text-xs font-medium ${
                  showWeeks ? 'text-foreground-secondary' : 'text-foreground-primary'
                }`}>
                  {marker.label}
                </div>
                {showWeeks && (
                  <div className="text-xs text-foreground-tertiary">
                    {(marker as any).month}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Timeline labels */}
      <div className="absolute top-2 left-2">
        <div className="text-xs text-foreground-tertiary">
          {startDate.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: 'short', 
            year: 'numeric' 
          })}
        </div>
      </div>
      
      <div className="absolute top-2 right-2">
        <div className="text-xs text-foreground-tertiary">
          {endDate.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: 'short', 
            year: 'numeric' 
          })}
        </div>
      </div>
    </div>
  );
};